import { useState, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Loader2, Compass, RefreshCw, Zap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { navHealthApi, type ReprocessProgress } from "@/api/nav-health";

export default function NavHealth() {
  const queryClient = useQueryClient();
  const [reprocessing, setReprocessing] = useState(false);
  const [progress, setProgress] = useState<ReprocessProgress | null>(null);
  const [rescrapeMax, setRescrapeMax] = useState(50);
  const [rescraping, setRescraping] = useState(false);
  const abortRef = useRef<(() => void) | null>(null);

  const { data: coverage, isLoading } = useQuery({
    queryKey: ["nav-coverage"],
    queryFn: () => navHealthApi.getCoverage(),
    refetchInterval: 30000,
  });

  const handleReprocess = useCallback(async () => {
    setReprocessing(true);
    setProgress(null);
    try {
      const { stream, abort } = navHealthApi.batchReprocess({ mode: "all" });
      abortRef.current = abort;
      if (!stream) { toast.error("No stream returned"); return; }

      const reader = stream.getReader();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setProgress(value);
        if (value.type === "done") {
          toast.success(`Done: ${value.updated} updated, ${value.skipped} skipped`);
        }
      }
      queryClient.invalidateQueries({ queryKey: ["nav-coverage"] });
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        toast.error(`Reprocess failed: ${(err as Error).message}`);
      }
    } finally {
      setReprocessing(false);
      abortRef.current = null;
    }
  }, [queryClient]);

  const handleRescrape = useCallback(async () => {
    setRescraping(true);
    try {
      const result = await navHealthApi.batchRescrape({
        mode: "all",
        max_count: rescrapeMax,
      });
      toast.success(`${result.queued} tasks queued for BrowserAct rescrape`);
      queryClient.invalidateQueries({ queryKey: ["nav-coverage"] });
    } catch (err) {
      toast.error(`Rescrape failed: ${(err as Error).message}`);
    } finally {
      setRescraping(false);
    }
  }, [rescrapeMax, queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const c = coverage;
  const progressPct = progress ? Math.round((progress.processed / Math.max(progress.total, 1)) * 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Compass className="h-6 w-6" />
          Nav Health
          <span className="text-base font-normal text-muted-foreground ml-2">導覽列健康度</span>
        </h1>
      </div>

      {/* Coverage Stats */}
      {c && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Coverage 覆蓋率</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{c.coverage_pct}%</div>
              <Progress value={c.coverage_pct} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Has Nav 有導覽</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{c.has_nav.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground mt-1">
                5+ items: {c.has_nav_5plus.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">Missing 待修復</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-orange-500">{c.zero_nav.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground mt-1">
                of {c.total.toLocaleString()} total
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Fixable Breakdown */}
      {c && c.zero_nav > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Fixable Breakdown 可修復細項</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Reprocess from markdown */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  <span>{c.fixable.has_markdown} pages</span>
                  <Badge variant="secondary">has markdown</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Re-extract nav from existing markdown content
                </p>
              </div>
              <Button
                onClick={handleReprocess}
                disabled={reprocessing || c.fixable.has_markdown === 0}
              >
                {reprocessing ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                ) : (
                  <>Re-extract</>
                )}
              </Button>
            </div>

            {/* Progress bar */}
            {progress && (
              <div className="p-4 border rounded-lg bg-muted/50 space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progress: {progress.processed} / {progress.total}</span>
                  <span>{progressPct}%</span>
                </div>
                <Progress value={progressPct} />
                <div className="flex gap-4 text-sm text-muted-foreground">
                  <span className="text-green-600">{progress.updated} updated</span>
                  <span>{progress.skipped} skipped</span>
                  {(progress.errors ?? 0) > 0 && (
                    <span className="text-red-500">{progress.errors} errors</span>
                  )}
                  {progress.duration_ms != null && (
                    <span>{(progress.duration_ms / 1000).toFixed(1)}s</span>
                  )}
                </div>
              </div>
            )}

            {/* BrowserAct rescrape */}
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <div className="font-medium flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  <span>{c.fixable.needs_rescrape} pages</span>
                  <Badge variant="destructive">needs rescrape</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  BrowserAct re-crawl (consumes credits)
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <span className="text-sm text-amber-600">
                    Est. {rescrapeMax * 10} credits
                  </span>
                  <span className="text-sm text-muted-foreground">Max:</span>
                  <Input
                    type="number"
                    min={1}
                    max={200}
                    value={rescrapeMax}
                    onChange={(e) => setRescrapeMax(Math.min(200, Math.max(1, parseInt(e.target.value) || 50)))}
                    className="w-20 h-8"
                  />
                </div>
              </div>
              <Button
                variant="destructive"
                onClick={handleRescrape}
                disabled={rescraping || c.fixable.needs_rescrape === 0}
              >
                {rescraping ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Queuing...</>
                ) : (
                  <>Queue Rescrape</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Breakdown by Reason */}
      {c && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Breakdown by Reason 原因分類</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {Object.entries(c.breakdown_by_reason).map(([key, count]) => (
                <div key={key} className="flex justify-between items-center p-3 border rounded">
                  <span className="text-sm font-medium capitalize">{key.replace(/_/g, ' ')}</span>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
